import {AxiosResponse} from "axios";
import {trackPromise} from "react-promise-tracker";
import {PaginatedResult} from "../models/default/paginated-result";
import Api from "./api-config";
import {HistoryResult} from "../models/default/history-result";
import {replace} from "formik";
import {ErrorComponent} from "../components/error/error.component";
import download from "downloadjs";

export interface Dictionary {
    [index: string]: string | number | any;
}


export class BaseService<T> {
    path: string;
    parameters = new URLSearchParams();
    private token: string | undefined;
    private static pollings: NodeJS.Timeout[] = [];


    constructor(path: string) {
        this.path = path;
    }


    getOptions(responseType?: Dictionary | any, isFormData = false): Dictionary {
        const httpOptions: Dictionary = {};
        const token = this.token ? this.token : sessionStorage.getItem("token");
        httpOptions["headers"] = {};
        if (token) {
            httpOptions["headers"]["Authorization"] = `Bearer ${token}`;
        }
        if (this.parameters) {
            httpOptions["params"] = this.parameters;
        }
        if (responseType) {
            httpOptions["responseType"] = responseType;
        }
        if (isFormData) {
            httpOptions["headers"]["Content-Type"] = "multipart/form-data";
        }
        const language = localStorage.getItem("lang");
        if (language) {
            const languageMappings: { [key: string]: string } = {
                pt: "pt-br",
                en: "en-us",
            };
            httpOptions["headers"]["Accept-Language"] = languageMappings[language] || language;
        }
        return httpOptions;
    }

    mountFormData(payload: any, files?: File[]): FormData {
        const newObject: Dictionary = {};
        Object.assign(newObject, payload);
        const formData = new FormData();
        // add files
        if (files != null) {
            Array.from(files).forEach(file => formData.append(file.name, file));
        }
        // add attr object
        if (payload != null) {
            Object.keys(newObject).forEach((key) => {
                if (newObject[key] != null) {
                    formData.append(key, newObject[key]);
                }
            });
        }
        return formData;
    }

    addParameter(key: string, value: any) {
        if (this.parameters.has(key)) {
            this.parameters.set(key, value);
        } else {
            this.parameters.append(key, value);
        }
    }

    clearParameters(): void {
        this.parameters = new URLSearchParams();
        this.token = "";
    }

    replaceParams(params?: Record<string, string | number>): string {
        let path = this.path;

        if (params) {
            for (const [key, value] of Object.entries(params)) {
                path = path.replace(`:${key}`, value.toString());
            }
        }
        return path
    }

    createPolling<T>(
        interval: number,
        fetchFunction: () => Promise<any>,
        callback: (response: any) => void,
        finalized: () => void
    ): NodeJS.Timeout {
        this.stopAllPollings();

        const polling = setInterval(() => {
            trackPromise(
                fetchFunction()
                    .then(response => {
                        callback(response);
                    })
                    .catch(error => {
                        ErrorComponent(error);
                    })
                    .finally(() => {
                        finalized();
                    }
            ));
        }, interval);
        BaseService.pollings.push(polling);
        return polling;
    }

    stopPolling(polling: NodeJS.Timeout): void {
        clearInterval(polling);
        BaseService.pollings = BaseService.pollings.filter(p => p !== polling);
    }

    stopAllPollings(): void {
        BaseService.pollings.forEach(polling => clearInterval(polling));
        BaseService.pollings = [];
    }

    async save(aObject: T | null, isFormData = false, params?: Record<string, string | number>, files?: File[]): Promise<AxiosResponse<T>> {
        let path = this.path;
        path = this.replaceParams(params);
        if (isFormData) {
            const formData = this.mountFormData(aObject, files);
            return await trackPromise(Api.post<T>(`${this.path}`, formData,
                this.getOptions(null, true)));
        } else {
            return await trackPromise(Api.post<T>(path, aObject, this.getOptions()));
        }
        // return this.mockResponse(aObject) as AxiosResponse<T>;
    }

    async postFromListRoute(payload: Dictionary, route: string, isFormData = false, files?: File[]):
        Promise<AxiosResponse<T>> {
        // if (isFormData) {
        //     const formData = this.mountFormData(files);
        //     return await trackPromise(Api.post<T>(`${this.path}${route}/`, formData,
        //         this.getOptions(null, true)));
        // } else {
        //     return await trackPromise(Api.post<T>(`${this.path}${route}/`, payload, this.getOptions()));
        // }
        return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async saveFromDetailRoute(
        payload: Dictionary,
        id: number,
        route: string,
        isFormData = false,
        files?: File[]): Promise<AxiosResponse<T>> {
        // if (isFormData) {
        //     const formData = this.mountFormData(payload, files);
        //     return await trackPromise(Api.post<T>(`${this.path}${id}/${route}/`, formData,
        //         this.getOptions(null, true)));
        // } else {
        //     return await trackPromise(Api.post<T>(`${this.path}${id}/${route}/`, payload, this.getOptions()));
        // }
        return this.mockResponse(payload) as AxiosResponse<T>;
    }


    async saveFromRoute(
        payload: Dictionary,
        route: string,
        isFormData = false, files?: File[]): Promise<AxiosResponse<T>> {
        // if (isFormData) {
        //     const formData = this.mountFormData(payload, files);
        //     return await trackPromise(Api.post<T>(`${this.path}${route}/`, formData,
        //         this.getOptions(null, true)));
        // } else {
        //     return await trackPromise(Api.post<T>(`${this.path}${route}/`, payload, this.getOptions()));
        // }
        return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async update(payload: Dictionary, id: number): Promise<AxiosResponse<T>> {
        // return await trackPromise(Api.patch<T>(`${this.path}${id}/`, payload, this.getOptions()));
        return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async patch(payload: Dictionary, id: number): Promise<AxiosResponse<T>> {
        return await trackPromise(Api.patch<T>(`${this.path}${id}/`, payload, this.getOptions()));
        // return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async getAllPaginate(params?: Record<string, string | number>): Promise<AxiosResponse<PaginatedResult<T>>> {
        let path = this.path;
        path = this.replaceParams(params);
        return await trackPromise(Api.get(path, this.getOptions()));
    }

    async getHistory(idHistory: number): Promise<AxiosResponse<HistoryResult[]>> {
        return await trackPromise(Api.get(`${this.path}${idHistory}/history/`, this.getOptions()));
    }

	async changePassword(payload: Dictionary, id: number, newPassword: string, route: string):
		Promise<AxiosResponse<T>> {
		return await trackPromise(Api.patch<T>(`${this.path}/change_password/`, payload, this.getOptions()));
	}

    async getAll(params?: Record<string, string | number>): Promise<AxiosResponse<T[]>> {
        let path = this.path;
        path = this.replaceParams(params);
        return await trackPromise(Api.get(path, this.getOptions()));
        // return this.mockResponseList() as AxiosResponse<T[]>;
    }

    async get(): Promise<AxiosResponse<T>> {
        return await trackPromise(Api.get(this.path, this.getOptions()));
        // return this.mockResponseList() as AxiosResponse<T>;
    }

    async getById(id: string | number): Promise<AxiosResponse<T>> {
        return await trackPromise(Api.get<T>(`${this.path}${id}/`, this.getOptions()).catch(ex => ex.response));
        // return this.mockResponse({}) as AxiosResponse<T>;
    }

    async getByIdIncluded(id: string | number): Promise<AxiosResponse<T>> {
        let path = this.path;
        if (id) {
            path = this.path.replace(':id', id.toString());
        }
        return await trackPromise(Api.get<T>(path, this.getOptions()).catch(ex => ex.response));
        // return this.mockResponse({}) as AxiosResponse<T>;
    }

    async loadUrl(url: string): Promise<AxiosResponse<T>> {
        // return await trackPromise(Api.get<T>(url, this.getOptions()).catch(ex => ex.response));
        return this.mockResponse({}) as AxiosResponse<T>;
    }

    async getAllFromListRoute(route: string): Promise<AxiosResponse<T[]>> {
        return await trackPromise(Api.get(`${this.path}${route}/`, this.getOptions()));
        // return this.mockResponseList() as AxiosResponse<T[]>;
    }

    async getFromListRoute(route: string): Promise<AxiosResponse<T>> {
        // return await trackPromise(Api.get(`${this.path}${route}/`, this.getOptions()));
        return this.mockResponse({}) as AxiosResponse<T>;
    }

    async getFromRoutePatch(route: string, payload: Dictionary): Promise<AxiosResponse<T>> {
        // return await trackPromise(Api.patch(`${this.path}${route}/`, payload, this.getOptions()));
        return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async getFromRoutePost(route: string, payload: Dictionary): Promise<AxiosResponse<T>> {
        // return await trackPromise(Api.post(`${this.path}${route}/`, payload, this.getOptions()));
        return this.mockResponse(payload) as AxiosResponse<T>;
    }

    async delete(id: string | number): Promise<AxiosResponse<void>> {
        return await trackPromise(Api.delete<void>(`${this.path}${id}/`, this.getOptions()));
    }

    async getChoices(field: string, options?: Dictionary, isFormData = false): Promise<AxiosResponse<T>> {
        // const mergedOptions = Object.assign({},
        //     this.getOptions(undefined, isFormData), options);
        // const response = await trackPromise(Api.options<any>(this.path, mergedOptions));
        // const actions = response.data?.actions?.POST;
        //
        // if (actions && actions[field] && actions[field].choices) {
        //     return actions[field].choices;
        // } else {
        //     throw new Error(`Invalid response structure for field: ${field}`);
        // }
        return this.mockResponse({}) as AxiosResponse<T>;
    }

    // async saveFromListRoute(aObject: T | null, route: string, isFormData =
    // false, files?: File[]): Promise<AxiosResponse<T>> {
    //     if (isFormData) {
    //         const formData = this.mountFormData(aObject, files);
    //         return await trackPromise(Api.post<T>(`${this.path}/${route}`, formData, this.getOptions(null, true)));
    //     } else {
    //         return await trackPromise(Api.post<T>(`${this.path}/${route}`, aObject, this.getOptions()));
    //     }
    // }

    // async updateFromDetailRoute(payload: Dictionary, id: number, route: string, isFormData =
    // false, files?: File[]): Promise<AxiosResponse<T>> {
    //     if (isFormData) {
    //         const formData = this.mountFormData(payload, files);
    //         return await trackPromise(Api.put<T>(`${this.path}/${id}/${route}`, formData,
    //         this.getOptions(null, true)));
    //     } else {
    //         return await trackPromise(Api.put<T>(`${this.path}/${id}/${route}`, payload, this.getOptions()));
    //     }
    // }

    async getFromDetailRoute(id: string | number, route: string): Promise<AxiosResponse<T>> {
        return await trackPromise(Api.get<T>(`${this.path}${id}/${route}/`, this.getOptions()));
    }

    // async uploadFromDetailRoute(id: number | string, route: string, files: File[]): Promise<AxiosResponse<T>> {
    //     const formData = new FormData();
    //     // add files
    //     Array.from(files).forEach(file => formData.append(file.name, file));
    //     return await trackPromise(Api.put<T>(`${this.path}/${id}/${route}`, formData, this.getOptions(null, true)));
    // }

    async uploadFile(route: string, files: File[]): Promise<AxiosResponse<T>> {
        const formData = new FormData();
        Array.from(files).forEach(file => {
            formData.append("file_name", file.name);
            formData.append("file", file);
        });
        return await trackPromise(Api.post<T>(`${this.path}${route}/`, formData, this.getOptions(null, true)));
        // return this.mockResponse({}) as AxiosResponse<T>;
    }

    // async loadFileFromDetailRoute(id: string | number, route: string): Promise<AxiosResponse<Blob>> {
    //     return await trackPromise(Api.get<Blob>(`${this.path}/${id}/${route}`, this.getOptions("blob")));
    // }

    async loadFile(params?: Record<string, string | number>): Promise<AxiosResponse<Blob>> {
        let path = this.path;
        path = this.replaceParams(params);
        return await trackPromise(Api.get<any>(`${path}`, this.getOptions("blob" as "json")));
    }

    downloadFile(blob: any) {
        // const url = window.URL.createObjectURL(blob);
        // const a = document.createElement("a");
        // a.href = url;
        // a.download = "file";
        // a.click();
        // window.URL.revokeObjectURL(url);
        if (blob) {
            download(
                new Blob([blob.data], {type: blob.data.type}),
                blob.headers['content-disposition'].match(/filename="(.+)"/)[1],
            );
        }

    }

    // async getFromListRoute(route: string): Promise<AxiosResponse<T>> {
    //     return await trackPromise(Api.get(`${this.path}/${route}`, this.getOptions()));
    // }

    // async getPaginatedFromListRoute(route: string): Promise<AxiosResponse<PaginatedResult<T>>> {
    //     return await trackPromise(Api.get(`${this.path}/${route}`, this.getOptions()));
    // }

    private mockResponse<T>(aObject: T): AxiosResponse<T, any> {
        return {
            data: aObject, // O objeto enviado como a resposta simulada
            status: 200, // Simula um status de sucesso
            statusText: 'OK',
            headers: {},
            config: {},
        };
    }

    private mockResponseList<T>(): AxiosResponse<T[] | undefined> {
        return {
            data: undefined, // `data` é opcional
            status: 200, // Simula um status de sucesso
            statusText: 'OK',
            headers: {},
            config: {},
        };
    }

}
